import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { envVariables } from '@fabric-ui/utils';
import * as Sentry from '@sentry/react';
import { CodeOwner } from '@utils/router.utils';
import kfuseRumSDK from 'kf-browser-sdk';
import { type AvRouter } from '../RouterInit';
import { getExporterWorkflowMetadata, isExportMode } from './exportDashboards.utils';

const isSynthetics =
  window._DATADOG_SYNTHETICS_BROWSER || // eslint-disable-line no-underscore-dangle
  navigator.userAgent.includes('Datadog') ||
  navigator.userAgent.includes('checklyhq.com');

const isDebugMode = () => {
  const resetDebugModeCount = Number(localStorage.getItem('resetDebugModeCount'));
  if (!['prod-se01', 'prod'].includes(envVariables.VITE_ENV) || resetDebugModeCount > 0) {
    return localStorage.getItem('debugMode') === 'true';
  }
  localStorage.setItem('debugMode', 'false');
  localStorage.setItem('resetDebugModeCount', `${resetDebugModeCount + 1}`);
  return false;
};

const shouldRecordSession = !isDebugMode() && !isSynthetics && !envVariables.STORYBOOK && envVariables.VITE_DD_CLIENT_TOKEN;

const getBody = (context: any) => {
  try {
    return context.requestInit?.body && typeof context.requestInit.body === 'string' && JSON.parse(context.requestInit.body);
  } catch (e) {
    console.warn(e, context.requestInit?.body);
    return '<body failed to parse>';
  }
};

const getFetchDetails = (context: any) => ({
  request: { graphqlname: context.requestInit?.headers?.graphqlname, body: getBody(context) },
  response: { headers: Object.fromEntries(context.response?.headers || []) },
});

const getOwnerByErrorMessage = ({
  message,
  handling_stack: stackTrace,
}: {
  message: string;
  handling_stack?: string;
}): CodeOwner | undefined => {
  const isQueryError = ['queryObjectAsync', 'queryObject', 'querySql'].some(term => message.includes(term));

  if ((message.includes('Effect failure') || message.includes('Failed to fetch')) && isQueryError) return CodeOwner.Query;
  if (stackTrace?.includes('pendo.js') || message.includes('[Pendo]')) return CodeOwner.Pendo;
  if (message.includes('[debugging enabled]')) return CodeOwner.Debugging;
  if (message.includes('Uncaught "Empty reason"') || message.includes('[Context Error]')) return CodeOwner.Frontend;
  return undefined;
};

const getOwnerFromStaticRoute = ({ matches }): CodeOwner | undefined =>
  matches.findLast(({ staticData }) => staticData?.owner)?.staticData.owner;

const createAvRum = () => {
  const state = { isInitialized: false, isKfuseEnabled: false, isSentryEnabled: false, router: null as AvRouter | null };

  const getOwner = () => getOwnerFromStaticRoute(state.router!.state) || CodeOwner.Frontend;

  const setOwnerContext = (owner: CodeOwner) => {
    datadogRum.setGlobalContextProperty('owner', owner);
    if (state.isKfuseEnabled) {
      kfuseRumSDK.setGlobalContext({ owner });
    }
    if (state.isSentryEnabled) {
      Sentry.setTag('owner', owner);
    }
  };

  const init = (routerInstance: AvRouter) => {
    if (!shouldRecordSession) return;

    state.router = routerInstance;
    state.isInitialized = true;

    const commonConfig = {
      service: 'app-avalor',
      env: envVariables.VITE_ENV,
      version: envVariables.VITE_VERSION,
    };

    // Initialize Datadog RUM
    datadogRum.init({
      ...commonConfig,
      applicationId: '79576ecf-b1bc-4bf0-ab5f-a99515bd4b2c',
      clientToken: envVariables.VITE_DD_CLIENT_TOKEN,
      site: 'datadoghq.com',
      sessionReplaySampleRate: 100,
      compressIntakeRequests: true,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'allow',
      plugins: [reactPlugin({ router: false })],
      beforeSend: (event, context: any) => {
        const owner = (event.type === 'error' && getOwnerByErrorMessage(event.error)) || getOwner();
        const fetchDetails = event.type === 'resource' && event.resource.type === 'fetch' ? getFetchDetails(context) : {};

        // eslint-disable-next-line no-param-reassign
        event.context = { ...event.context, owner, ...fetchDetails };
        return true;
      },
    });

    // Initialize Kfuse RUM if configured
    const shouldInitializeKfuse = false;
    if (shouldInitializeKfuse && envVariables.VITE_KFUSE_APPLICATION_ID) {
      state.isKfuseEnabled = true;
      kfuseRumSDK.init({
        config: {
          ...commonConfig,
          applicationId: envVariables.VITE_KFUSE_APPLICATION_ID,
          clientToken: envVariables.VITE_KFUSE_CLIENT,
          proxy: envVariables.VITE_KFUSE_URL,
          sessionSampleRate: 100,
          defaultPrivacyLevel: 'allow',
          enableSessionRecording: true,
          enableLogCollection: true,
        },
      });
    }

    datadogRum.startSessionReplayRecording();

    // Initialize Sentry RUM if configured
    if (envVariables.VITE_SENTRY_DSN) {
      state.isSentryEnabled = true;
      Sentry.init({
        dsn: envVariables.VITE_SENTRY_DSN,
        environment: commonConfig.env,
        release: commonConfig.version,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.captureConsoleIntegration({ levels: ['error'] }),
          Sentry.replayIntegration({
            maskAllText: false,
            blockAllMedia: false,
            networkDetailAllowUrls: [envVariables.VITE_API_URL],
            networkRequestHeaders: ['graphqlname', 'x-request-id', 'traceparent', 'x-b3-traceid'],
            networkResponseHeaders: ['x-request-id', 'traceparent', 'x-b3-traceid'],
          }),
        ],
        tracesSampleRate: 1.0,
        replaysSessionSampleRate: 1.0,
        replaysOnErrorSampleRate: 1.0,
        beforeSend: (event, hint) => {
          const req = hint?.originalException;
          const owner = (req instanceof Error && getOwnerByErrorMessage({ message: req.message })) || getOwner();
          // eslint-disable-next-line no-param-reassign
          event.tags = { ...event.tags, owner };
          return event;
        },
      });
    }

    // Set initial owner context for all providers
    setOwnerContext(getOwner());
  };

  const setUser = (userData: Record<string, any>) => {
    if (!state.isInitialized) return;

    const currentUser = datadogRum.getUser();
    const { userName, userId, ...rest } = userData;
    const commonData = { ...currentUser, ...rest, ...(userName && { name: userName }) };

    // Set Datadog user
    datadogRum.setUser({ ...commonData, ...(userId && { userId }) });

    // Set Kfuse user (userId maps to id)
    if (state.isKfuseEnabled) {
      kfuseRumSDK.setUser({ ...commonData, ...(userId && { id: userId }) });
    }

    // Set Sentry user (userId maps to id)
    if (state.isSentryEnabled) {
      Sentry.setUser({ ...commonData, ...(userId && { id: String(userId) }) });
    }
  };

  const addAction = (name: string, context?: object) => {
    if (!state.isInitialized) return;
    const owner = getOwner();
    datadogRum.addAction(name, { ...context, owner });
    if (state.isKfuseEnabled) {
      kfuseRumSDK.addAction?.(name, { ...context, owner });
    }
    if (state.isSentryEnabled) {
      Sentry.addBreadcrumb({ category: 'action', message: name, data: { ...context, owner } });
    }
  };

  const addError = (error: unknown, context?: object) => {
    if (!state.isInitialized) return;
    const owner = getOwner();
    datadogRum.addError(error, { ...context, owner });
    if (state.isSentryEnabled) {
      Sentry.captureException(error, { extra: { ...context, owner } });
    }
  };

  return {
    init,
    setUser,
    getUser: () => (state.isInitialized ? datadogRum.getUser() : {}),
    addAction,
    addError,
    setGlobalContextProperty: (key: string, value: any) => {
      if (state.isInitialized) {
        datadogRum.setGlobalContextProperty(key, value);
        if (state.isKfuseEnabled) kfuseRumSDK.setGlobalContext?.({ [key]: value });
        if (state.isSentryEnabled) Sentry.setTag(key, value);
      }
    },
    removeGlobalContextProperty: (key: string) => {
      if (state.isInitialized) {
        datadogRum.removeGlobalContextProperty(key);
        if (state.isSentryEnabled) Sentry.setTag(key, undefined);
      }
    },
    get isRecording() {
      return shouldRecordSession;
    },
  };
};

export const avRum = createAvRum();

export { isDebugMode, shouldRecordSession, isSynthetics };

export const initRum = (routerInstance: AvRouter) => {
  avRum.init(routerInstance);

  if (isExportMode() && shouldRecordSession) {
    avRum.setUser({ ...avRum.getUser(), ...getExporterWorkflowMetadata() });
  }
};
